/**
 * generatePreProcessJsonMetadata_Classification.ts - Classification methods for metadata generation
 * Port of C# GeneratePreProcessJsonMetadata_Classification.cs
 * Pure ES module - works in both Node.js and browser via bundler
 */

import type { TemplateInfo, SlotInfo } from '../../model/appSiteMetadata.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonObject.js';

const JSON_INHERIT_KEY_SUFFIX = '#';

/**
 * Recursively classifies placeholders and slotholders in a template hierarchy
 */
export function classifyPlaceholdersRecursive(template: TemplateInfo): void {
    const placeholdersToRemove: string[] = [];

    // Check regular placeholders
    for (const placeholder of (template.placeholders || [])) {
        // Check if this placeholder has a corresponding component in template.components
        // Match against placeholderName (what the parent calls it) not templateName (actual template name)
        let hasComponent = false;
        for (const component of (template.components || [])) {
            // Check placeholderName first (handles AppView replacements like MainContent->NextContent)
            if (component.placeholderName &&
                component.placeholderName.toLowerCase() === placeholder.toLowerCase()) {
                hasComponent = true;
                break;
            }
            // Fallback: check templateName (for cases where placeholderName matches templateName)
            if (component.templateName && component.templateName.toLowerCase() === placeholder.toLowerCase()) {
                hasComponent = true;
                break;
            }
        }

        // If no component found, move to nonPlaceholders
        if (!hasComponent) {
            template.nonPlaceholders = template.nonPlaceholders || [];
            template.nonPlaceholders.push(placeholder);
            placeholdersToRemove.push(placeholder);
        }
    }

    // Remove from placeholders
    for (const placeholder of placeholdersToRemove) {
        const index = template.placeholders.indexOf(placeholder);
        if (index > -1) {
            template.placeholders.splice(index, 1);
        }
    }

    // Check slotholders - need to check if slot components exist
    const slotholdersToRemove: SlotInfo[] = [];
    for (const slotholder of (template.slotholders || [])) {
        // Extract slot name without the # prefix
        const slotName = slotholder.name.replace(/^#/, '');

        // Check if this slotholder has a corresponding component
        // Match against placeholderName (what the parent calls it) not templateName (actual template name)
        let hasComponent = false;
        for (const component of (template.components || [])) {
            // Check placeholderName first (handles AppView replacements)
            if (component.placeholderName &&
                component.placeholderName.toLowerCase() === slotName.toLowerCase()) {
                hasComponent = true;
                break;
            }
            // Fallback: check templateName
            if (component.templateName && component.templateName.toLowerCase() === slotName.toLowerCase()) {
                hasComponent = true;
                break;
            }
        }

        // If no component found, move to nonSlotholders
        if (!hasComponent) {
            template.nonSlotholders = template.nonSlotholders || [];
            template.nonSlotholders.push(slotholder);
            slotholdersToRemove.push(slotholder);
        }
    }

    // Remove from slotholders
    for (const slotholder of slotholdersToRemove) {
        const index = template.slotholders.indexOf(slotholder);
        if (index > -1) {
            template.slotholders.splice(index, 1);
        }
    }

    // Recursively classify child components
    for (const component of (template.components || [])) {
        classifyPlaceholdersRecursive(component);
    }
}

/**
 * Collects JSON key names used in HTML template and identifies missing keys
 */
export function classifyJsonKeys(
    templateInfo: TemplateInfo,
    jsonKeyValues: JsonObject,
    htmlContent: string
): void {
    if (!jsonKeyValues) return;
    // Find all JsonKeys in HTML content (format: {{$KeyName}})
    const allJsonKeysInHtml: string[] = [];
    let startIndex = 0;
    while (true) {
        const keyStart = htmlContent.indexOf('{{$', startIndex);
        if (keyStart < 0) break;

        const keyEnd = htmlContent.indexOf('}}', keyStart + 3);
        if (keyEnd < 0) break;

        const key = htmlContent.substring(keyStart + 3, keyEnd);
        if (!allJsonKeysInHtml.includes(key)) {
            allJsonKeysInHtml.push(key);
        }

        startIndex = keyEnd + 2;
    }

    // Find all JsonArrayKeys in HTML content (format: {{@ArrayKey}})
    // Exclude slotholder placeholders like {{@HTMLPLACEHOLDER}}
    const allArrayKeysInHtml: string[] = [];
    startIndex = 0;
    while (true) {
        const keyStart = htmlContent.indexOf('{{@', startIndex);
        if (keyStart < 0) break;

        const keyEnd = htmlContent.indexOf('}}', keyStart + 3);
        if (keyEnd < 0) break;

        const key = htmlContent.substring(keyStart + 3, keyEnd);

        // Skip slotholder placeholders (they use @HTMLPLACEHOLDER syntax but aren't array keys)
        if (key.toUpperCase() !== 'HTMLPLACEHOLDER') {
            if (!allArrayKeysInHtml.includes(key)) {
                allArrayKeysInHtml.push(key);
            }
        }

        startIndex = keyEnd + 2;
    }

    // Find all ConditionalKeys in HTML content (format: {{?CondKey}})
    const allConditionalKeysInHtml: string[] = [];
    startIndex = 0;
    while (true) {
        const keyStart = htmlContent.indexOf('{{?', startIndex);
        if (keyStart < 0) break;

        const keyEnd = htmlContent.indexOf('}}', keyStart + 3);
        if (keyEnd < 0) break;

        const key = htmlContent.substring(keyStart + 3, keyEnd);
        if (!allConditionalKeysInHtml.includes(key)) {
            allConditionalKeysInHtml.push(key);
        }

        startIndex = keyEnd + 2;
    }

    // Initialize arrays if not present
    templateInfo.jsonKeys = templateInfo.jsonKeys || [];
    templateInfo.nonJsonKeys = templateInfo.nonJsonKeys || [];
    templateInfo.jsonInheritKeys = templateInfo.jsonInheritKeys || new Map();
    templateInfo.jsonArrayKeys = templateInfo.jsonArrayKeys || [];
    templateInfo.nonJsonArrayKeys = templateInfo.nonJsonArrayKeys || [];

    // Classify each JsonKey as either jsonKeys (available in JSON) or nonJsonKeys (missing from JSON)
    for (const key of allJsonKeysInHtml) {
        // Check if key exists in JSON (either with or without inheritance suffix)
        const keyWithInheritSuffix = key + JSON_INHERIT_KEY_SUFFIX;
        const hasDirectKey = jsonKeyValues.has(key);
        const hasInheritKey = jsonKeyValues.has(keyWithInheritSuffix);

        if (hasDirectKey || hasInheritKey) {
            // Key exists in JSON (either direct or with # suffix) - add to jsonKeys
            if (!templateInfo.jsonKeys.includes(key)) {
                templateInfo.jsonKeys.push(key);
            }

            // If key has # suffix in JSON (inheritance marker), add to jsonInheritKeys
            // Source template will be resolved later after hierarchy is built
            if (hasInheritKey && !templateInfo.jsonInheritKeys.has(key)) {
                templateInfo.jsonInheritKeys.set(key, ''); // Empty for now, will be resolved after hierarchy building
            }
        } else {
            // Key does not exist in JSON - add to nonJsonKeys
            if (!templateInfo.nonJsonKeys.includes(key)) {
                templateInfo.nonJsonKeys.push(key);
            }
        }
    }

    // Classify each ArrayKey as either jsonArrayKeys or nonJsonArrayKeys
    // {{@Key}} pattern is used for both array iteration and boolean conditionals
    // The distinction is made during merging based on the actual JSON value type
    for (const key of allArrayKeysInHtml) {
        if (jsonKeyValues.has(key)) {
            // Key exists in JSON - add to jsonArrayKeys (includes both arrays and booleans)
            if (!templateInfo.jsonArrayKeys.includes(key)) {
                templateInfo.jsonArrayKeys.push(key);
            }
        } else {
            // Key does not exist in JSON - add to nonJsonArrayKeys
            if (!templateInfo.nonJsonArrayKeys.includes(key)) {
                templateInfo.nonJsonArrayKeys.push(key);
            }
        }
    }
}
